
import React, { useState, useEffect } from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from "@/integrations/supabase/client";
import { Badge as BadgeType } from '@/types/quests';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { Plus, Award } from 'lucide-react';
import BadgesList from './BadgesList';
import BadgeForm from './BadgeForm';

const BadgesManagement: React.FC = () => {
  const { t } = useTranslationUtils();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [badges, setBadges] = useState<BadgeType[]>([]);
  const [isAddingBadge, setIsAddingBadge] = useState(false);
  const [editingBadge, setEditingBadge] = useState<BadgeType | null>(null);

  useEffect(() => {
    fetchBadges();
  }, []);

  const fetchBadges = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('badges')
        .select('*')
        .order('name');
        
      if (error) throw error;
      
      setBadges(data || []);
    } catch (error) {
      console.error('Error fetching badges:', error);
      toast({
        title: t('admin.error'),
        description: t('admin.errorFetchingBadges'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddBadge = () => {
    setEditingBadge(null);
    setIsAddingBadge(true);
  };

  const handleEditBadge = (badge: BadgeType) => {
    setEditingBadge(badge);
    setIsAddingBadge(true);
  };

  const handleCancelForm = () => {
    setIsAddingBadge(false);
    setEditingBadge(null);
  };

  const handleBadgeSaved = () => {
    fetchBadges();
    setIsAddingBadge(false);
    setEditingBadge(null);
    
    toast({
      title: t('admin.success'),
      description: editingBadge 
        ? t('admin.badgeUpdated') 
        : t('admin.badgeCreated'),
    });
  };

  const handleDeleteBadge = async (badgeId: string) => {
    if (!confirm(t('admin.confirmDeleteBadge'))) return;
    
    try {
      // Check if badge is used in any quests
      const { data: questsData } = await supabase
        .from('quests')
        .select('id, name')
        .eq('badge_id', badgeId)
        .limit(1);
        
      if (questsData && questsData.length > 0) {
        toast({
          title: t('admin.cannotDeleteBadge'),
          description: t('admin.badgeUsedInQuests', { quest: questsData[0].name }),
          variant: 'destructive',
        });
        return;
      }
      
      // Check if badge is assigned to any users
      const { data: userBadgesData } = await supabase
        .from('user_badges')
        .select('id')
        .eq('badge_id', badgeId)
        .limit(1);
        
      if (userBadgesData && userBadgesData.length > 0) {
        toast({
          title: t('admin.cannotDeleteBadge'),
          description: t('admin.badgeAssignedToUsers'),
          variant: 'destructive',
        });
        return;
      }
      
      // Delete the badge if not in use
      const { error } = await supabase
        .from('badges')
        .delete()
        .eq('id', badgeId);
        
      if (error) throw error;
      
      toast({
        title: t('admin.success'),
        description: t('admin.badgeDeleted'),
      });
      
      fetchBadges();
    } catch (error) {
      console.error('Error deleting badge:', error);
      toast({
        title: t('admin.error'),
        description: t('admin.errorDeletingBadge'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Award className="h-5 w-5" />
          <span>{t('admin.badges')}</span>
        </CardTitle>
        
        {!isAddingBadge && (
          <Button 
            onClick={handleAddBadge} 
            className="flex items-center gap-1"
          >
            <Plus size={16} />
            <span>{t('admin.addBadge')}</span>
          </Button>
        )}
      </CardHeader>
      
      <CardContent>
        {isAddingBadge ? (
          <BadgeForm 
            editingBadge={editingBadge}
            onCancel={handleCancelForm}
            onSave={handleBadgeSaved}
          />
        ) : (
          <BadgesList 
            badges={badges}
            loading={loading}
            onEdit={handleEditBadge}
            onDelete={handleDeleteBadge}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default BadgesManagement;
